/**
 * tetris_run_autoplay - Agent JavaScript 自动播放工具
 * 允许Agent传送一段JavaScript代码，在页面中自主运行完成整个游戏
 */
/* eslint-disable */
var t = require('../lang').t;
var gameAPI = require('./autoplayAPI');
var store = require('../../store').default;
var states = require('../../control/states').default;

// 超时时间（毫秒）
var DEFAULT_TIMEOUT = 300000; // 5分钟

// 禁止的关键字列表（用于代码安全验证）
// 注意：只检测全局对象访问，不检测小写的语言关键字
var FORBIDDEN_KEYWORDS = [
  // 危险执行函数
  { pattern: /\beval\s*\(/, name: 'eval()' },
  { pattern: /new\s+Function\s*\(/, name: 'new Function()' },

  // DOM操作
  { pattern: /document\.write\s*\(/, name: 'document.write()' },
  { pattern: /document\.open\s*\(/, name: 'document.open()' },
  { pattern: /document\.close\s*\(/, name: 'document.close()' },
  { pattern: /document\.cookie/, name: 'document.cookie' },

  // Window操作
  { pattern: /window\.open\s*\(/, name: 'window.open()' },
  { pattern: /window\.close\s*\(/, name: 'window.close()' },
  { pattern: /window\.location/, name: 'window.location' },
  { pattern: /window\.parent/, name: 'window.parent' },
  { pattern: /window\.top/, name: 'window.top' },

  // 存储和网络
  { pattern: /\blocalStorage\b/, name: 'localStorage' },
  { pattern: /\bsessionStorage\b/, name: 'sessionStorage' },
  { pattern: /\bindexedDB\b/, name: 'indexedDB' },
  { pattern: /\bfetch\s*\(/, name: 'fetch()' },
  { pattern: /XMLHttpRequest/, name: 'XMLHttpRequest' },
  { pattern: /\bWebSocket\b/, name: 'WebSocket' },

  // 其他危险API
  { pattern: /\bWorker\b/, name: 'Worker' },
  { pattern: /importScripts\s*\(/, name: 'importScripts()' },
  { pattern: /\brequire\s*\(/, name: 'require()' },
  { pattern: /\bmodule\b/, name: 'module' },
  { pattern: /\bexports\b/, name: 'exports' },
  { pattern: /\bprocess\b/, name: 'process' },
  { pattern: /\bglobal\b/, name: 'global' },
  { pattern: /__proto__/, name: '__proto__' },
  { pattern: /\.constructor\s*[^(]/, name: '.constructor access' },
  { pattern: /callPhantom/, name: 'callPhantom' },
  { pattern: /_phantom/, name: '_phantom' },
];

/**
 * 验证代码安全性
 * @param {string} code - JavaScript代码
 * @returns {Object} 验证结果
 */
function validateCode(code) {
  if (!code || typeof code !== 'string') {
    return { valid: false, error: t('代码不能为空', 'Code cannot be empty') };
  }

  // 检查代码长度
  if (code.length > 50000) {
    return { valid: false, error: t('代码长度超过限制（最大50KB）', 'Code exceeds size limit (max 50KB)') };
  }

  // 检查禁止的关键字/模式
  for (var i = 0; i < FORBIDDEN_KEYWORDS.length; i++) {
    var item = FORBIDDEN_KEYWORDS[i];
    if (item.pattern.test(code)) {
      return { valid: false, error: t('代码包含禁止的API: ', 'Code contains forbidden API: ') + item.name };
    }
  }

  // 检查潜在的恶意代码模式
  var dangerousPatterns = [
    /<script\b/i,
    /javascript:/i,
    /on\w+\s*=/i,  // onclick, onload, etc.
    /data:text\/html/i,
  ];

  for (var j = 0; j < dangerousPatterns.length; j++) {
    if (dangerousPatterns[j].test(code)) {
      return { valid: false, error: t('代码包含潜在的危险模式', 'Code contains potentially dangerous patterns') };
    }
  }

  return { valid: true };
}

/**
 * 创建隔离的执行环境
 * @returns {Object} 执行上下文
 */
function createExecutionContext() {
  // 复制gameAPI到本地变量
  var api = {};
  for (var key in gameAPI) {
    if (gameAPI.hasOwnProperty(key)) {
      api[key] = gameAPI[key];
    }
  }

  // 返回允许访问的API
  return {
    gameAPI: api,
    console: {
      log: function() {
        var args = Array.prototype.slice.call(arguments);
        console.log('[Autoplay]', args.join(' '));
      },
      error: function() {
        var args = Array.prototype.slice.call(arguments);
        console.error('[Autoplay]', args.join(' '));
      },
      warn: function() {
        var args = Array.prototype.slice.call(arguments);
        console.warn('[Autoplay]', args.join(' '));
      },
    },
    setTimeout: setTimeout,
    clearTimeout: clearTimeout,
    setInterval: setInterval,
    clearInterval: clearInterval,
    Date: Date,
    Math: Math,
    JSON: JSON,
    Array: Array,
    Object: Object,
    String: String,
    Number: Number,
    Boolean: Boolean,
    Promise: Promise,
    Error: Error,
    parseInt: parseInt,
    parseFloat: parseFloat,
    isNaN: isNaN,
    isFinite: isFinite,
    encodeURIComponent: encodeURIComponent,
    decodeURIComponent: decodeURIComponent,
  };
}

/**
 * 包装用户代码，添加超时控制
 * @param {string} code - 用户代码
 * @param {number} timeout - 超时时间（毫秒）
 * @returns {string} 包装后的代码
 */
function wrapUserCode(code, timeout) {
  return `
    (async function() {
      var startTime = Date.now();
      var timeout = ${timeout};
      var checkTimeout = function() {
        if (Date.now() - startTime > timeout) {
          throw new Error('代码执行超时（${timeout}ms）');
        }
      };

      // 注入超时检查到setTimeout和setInterval
      var originalSetTimeout = setTimeout;
      setTimeout = function(fn, delay) {
        checkTimeout();
        return originalSetTimeout(fn, delay);
      };

      try {
        ${code}
      } catch (e) {
        console.error('执行错误:', e.message);
        throw e;
      }
    })()
  `;
}

/**
 * 执行自动播放脚本
 * @param {string} code - JavaScript代码
 * @param {number} timeout - 超时时间（毫秒）
 * @returns {Promise<Object>} 执行结果
 */
function runAutoplay(code, timeout) {
  timeout = timeout || DEFAULT_TIMEOUT;

  return new Promise(function(resolve, reject) {
    // 1. 验证代码安全性
    var validation = validateCode(code);
    if (!validation.valid) {
      resolve({
        success: false,
        message: t('代码验证失败: ', 'Code validation failed: ') + validation.error,
      });
      return;
    }

    // 2. 检查游戏状态，如果未开始则自动开始
    var state = store.getState();
    if (state.get('cur') === null || state.get('reset')) {
      states.start();
    }

    // 3. 标记自动播放状态
    window.__tetrisAutoplayActive = true;
    window.__tetrisAutoplayStartTime = Date.now();
    window.__tetrisAutoplayResult = null;

    // 4. 创建执行上下文
    var context = createExecutionContext();

    // 5. 包装代码
    var wrappedCode = wrapUserCode(code, timeout);

    // 6. 创建函数并执行
    try {
      // 创建参数列表
      var contextKeys = Object.keys(context);
      var contextValues = contextKeys.map(function(key) {
        return context[key];
      });

      // 创建函数
      var fn = new (Function.prototype.bind.apply(Function, [null].concat(contextKeys, [wrappedCode])))();

      // 设置超时
      var timeoutId = setTimeout(function() {
        window.__tetrisAutoplayActive = false;
        resolve({
          success: false,
          message: t('代码执行超时（', 'Code execution timed out (') + timeout + 'ms)',
        });
      }, timeout);

      // 执行函数
      var result = fn.apply(null, contextValues);

      // 处理Promise
      if (result && typeof result.then === 'function') {
        result
          .then(function(value) {
            clearTimeout(timeoutId);
            window.__tetrisAutoplayActive = false;
            if (!window.__tetrisAutoplayResult) {
              var currentState = store.getState();
              window.__tetrisAutoplayResult = {
                score: currentState.get('points') || 0,
                linesCleared: currentState.get('clearLines') || 0,
                level: currentState.get('speedRun') || 1,
                duration: Date.now() - window.__tetrisAutoplayStartTime,
                timestamp: Date.now(),
              };
            }
            resolve({
              success: true,
              message: t('代码执行完成', 'Code execution finished'),
              result: window.__tetrisAutoplayResult,
            });
          })
          .catch(function(error) {
            clearTimeout(timeoutId);
            window.__tetrisAutoplayActive = false;
            resolve({
              success: false,
              message: t('代码执行出错: ', 'Code execution error: ') + error.message,
              error: error.message,
            });
          });
      } else {
        clearTimeout(timeoutId);
        window.__tetrisAutoplayActive = false;
        // 同步执行完成
        resolve({
          success: true,
          message: t('代码执行完成（同步）', 'Code execution finished (sync)'),
          note: t('建议使用 async/await 编写代码以获得更好的控制', 'Use async/await for better control'),
        });
      }
    } catch (error) {
      window.__tetrisAutoplayActive = false;
      resolve({
        success: false,
        message: t('代码执行出错: ', 'Code execution error: ') + error.message,
        error: error.message,
      });
    }
  });
}

/**
 * 获取自动播放结果
 * @returns {Object} 游戏结果
 */
function getAutoplayResult() {
  var result = window.__tetrisAutoplayResult;

  if (!result) {
    // 如果还没有结果，返回当前游戏状态
    var state = store.getState();
    return {
      success: true,
      finished: false,
      active: !!window.__tetrisAutoplayActive,
      currentState: {
        score: state.get('points') || 0,
        linesCleared: state.get('clearLines') || 0,
        level: state.get('speedRun') || 1,
        isGameOver: state.get('reset') || false,
        duration: window.__tetrisAutoplayStartTime ?
          Date.now() - window.__tetrisAutoplayStartTime : 0,
      },
    };
  }

  return {
    success: true,
    finished: true,
    result: result,
  };
}

/**
 * 等待自动播放完成
 * @param {number} timeout - 等待超时时间
 * @returns {Promise<Object>} 游戏结果
 */
function waitForAutoplay(timeout) {
  timeout = timeout || DEFAULT_TIMEOUT;

  return new Promise(function(resolve) {
    // 检查是否已经有结果
    if (window.__tetrisAutoplayResult) {
      resolve({
        success: true,
        message: t('游戏已结束', 'Game over'),
        result: window.__tetrisAutoplayResult,
      });
      return;
    }

    // 检查是否在运行
    if (!window.__tetrisAutoplayActive) {
      resolve({
        success: false,
        message: t('没有正在运行的自动播放', 'No autoplay is running'),
      });
      return;
    }

    // 设置等待回调
    window.__tetrisAutoplayWaitResolve = function(result) {
      resolve(result);
    };

    // 设置超时
    setTimeout(function() {
      if (window.__tetrisAutoplayWaitResolve) {
        window.__tetrisAutoplayWaitResolve = null;
        resolve({
          success: false,
          message: t('等待超时（', 'Wait timed out (') + timeout + 'ms)',
        });
      }
    }, timeout);
  });
}

/**
 * WebMCP 工具定义 - 运行自动播放
 */
var runAutoplayTool = {
  name: 'tetris_run_autoplay',
  description: t('运行Agent JavaScript自动播放代码。Agent传送一段JavaScript代码，在页面中自主运行完成整个游戏，减少通信延迟。代码通过gameAPI对象控制游戏。', 'Run Agent JavaScript autoplay code. The Agent sends a JavaScript snippet that plays the whole game in-page, reducing round trips. Control the game via the gameAPI object.'),
  inputSchema: {
    type: 'object',
    properties: {
      script: {
        type: 'string',
        description: t('JavaScript代码字符串。可使用gameAPI对象控制游戏。', 'JavaScript code string. Use the gameAPI object to control the game.'),
      },
      timeout: {
        type: 'number',
        description: t('超时时间（毫秒），默认300000（5分钟）', 'Timeout in ms, default 300000 (5 min)'),
        default: 300000,
      },
    },
    required: ['script'],
  },
  execute: function(args) {
    args = args || {};
    var result = runAutoplay(args.script, args.timeout);
    return result.then(function(res) {
      return {
        content: [
          {
            type: 'text',
            text: JSON.stringify(res, null, 2),
          },
        ],
      };
    });
  },
};

/**
 * WebMCP 工具定义 - 获取自动播放结果
 */
var getAutoplayResultTool = {
  name: 'tetris_get_autoplay_result',
  description: t('获取自动播放的结果。如果没有结果，返回当前游戏状态。', 'Get the autoplay result. If none, returns the current game state.'),
  inputSchema: {
    type: 'object',
    properties: {},
  },
  execute: function() {
    var result = getAutoplayResult();
    return Promise.resolve({
      content: [
        {
          type: 'text',
          text: JSON.stringify(result, null, 2),
        },
      ],
    });
  },
};

/**
 * WebMCP 工具定义 - 等待自动播放完成
 */
var waitForAutoplayTool = {
  name: 'tetris_wait_for_autoplay',
  description: t('等待自动播放完成并返回结果。阻塞调用，直到游戏结束或超时。', 'Wait for autoplay to finish and return the result. Blocks until game over or timeout.'),
  inputSchema: {
    type: 'object',
    properties: {
      timeout: {
        type: 'number',
        description: t('等待超时时间（毫秒），默认300000（5分钟）', 'Wait timeout in ms, default 300000 (5 min)'),
        default: 300000,
      },
    },
  },
  execute: function(args) {
    args = args || {};
    return waitForAutoplay(args.timeout).then(function(result) {
      return {
        content: [
          {
            type: 'text',
            text: JSON.stringify(result, null, 2),
          },
        ],
      };
    });
  },
};

module.exports = {
  runAutoplayTool: runAutoplayTool,
  getAutoplayResultTool: getAutoplayResultTool,
  waitForAutoplayTool: waitForAutoplayTool,
};



// WEBPACK FOOTER //
// ./src/webmcp/tools/runAutoplay.js